import { z } from 'zod'

const MAX_REDIRECT_URLS = 5

type ApiClientFormValues = {
  clientName: string
  privacyPolicyUrl: string
  logoUri: string | null
  isPublicClient: boolean
  redirectUrls: Array<{ id: string; value: string }>
  scopeNames: string[]
}

const addHttpsUrlIssue = (value: string, context: z.RefinementCtx): void => {
  const trimmedValue = value.trim()

  if (!trimmedValue) return

  try {
    const url = new URL(trimmedValue)

    if (url.protocol !== 'https:') {
      context.addIssue({
        code: 'custom',
        message: 'URL must start with https://.',
      })
    }
  } catch {
    context.addIssue({
      code: 'custom',
      message: 'Please enter a valid HTTPS URL.',
    })
  }
}

const apiClientNameSchema = z.string().superRefine((value, context) => {
  const trimmedValue = value.trim()

  if (trimmedValue.length < 2) {
    context.addIssue({
      code: 'custom',
      message: 'Client name must be at least 2 characters long.',
    })
    return
  }

  if (!/^[a-zA-Z0-9\s]+$/.test(value)) {
    context.addIssue({
      code: 'custom',
      message: 'Client name can only contain letters and numbers.',
    })
    return
  }

  if (!/[a-zA-Z]/.test(value)) {
    context.addIssue({
      code: 'custom',
      message: 'Client name must contain at least one letter.',
    })
  }
})

const optionalHttpsUrlSchema = z.string().superRefine(addHttpsUrlIssue)

const redirectUrlSchema = z.object({
  id: z.string(),
  value: optionalHttpsUrlSchema,
})

const createApiClientFormSchema = (
  privacyPolicyRequired: boolean,
): z.ZodType<ApiClientFormValues, ApiClientFormValues> =>
  z
    .object({
      clientName: apiClientNameSchema,
      privacyPolicyUrl: optionalHttpsUrlSchema,
      logoUri: z.string().nullable(),
      isPublicClient: z.boolean(),
      redirectUrls: z.array(redirectUrlSchema).min(1).max(MAX_REDIRECT_URLS),
      scopeNames: z.array(z.string()),
    })
    .superRefine((values, context) => {
      if (privacyPolicyRequired && !values.privacyPolicyUrl.trim()) {
        context.addIssue({
          code: 'custom',
          message: 'Privacy policy URL is required.',
          path: ['privacyPolicyUrl'],
        })
      }

      if (values.redirectUrls.every(({ value }) => !value.trim())) {
        context.addIssue({
          code: 'custom',
          message: 'At least one redirect URL is required.',
          path: ['redirectUrls', 0, 'value'],
        })
      }
    })

export { apiClientNameSchema, createApiClientFormSchema, MAX_REDIRECT_URLS }
export type { ApiClientFormValues }
